/**
 * TrainingCatalogAdmin
 * 
 * Admin page for managing training artifacts and requirements.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// Batch 18-J9 — Provider Registry admin tab (operational UI for the
// I3/J-lane provider evidence callback registry). The panel calls
// only the J3 read RPC + J7 active-state / secret-rotation mutation
// RPCs; it never reads provider base tables directly and never
// displays raw secret material or raw event payloads.
import { TrainingProvidersAdminPanel } from '@/components/admin/TrainingProvidersAdminPanel';
// Batch 18-J12 — Provider Artifact Mapping admin tab. Adjacent to
// the J9 Training Providers tab. Reads via J3 RPCs and writes via
// the J6 upsert RPC; never touches provider tables directly.
import { ProviderArtifactMappingAdminPanel } from '@/components/admin/ProviderArtifactMappingAdminPanel';
// Batch 18-J15 — Provider User Mapping admin tab. Adjacent to the
// J12 artifact mapping tab. Reads via J3 user-mapping RPC + J6
// user-mapping upsert RPC; the only direct table touch is the safe
// shop-scoped `technicians` table for the technician picker.
import { ProviderUserMappingAdminPanel } from '@/components/admin/ProviderUserMappingAdminPanel';
// Batch 18-J18 — Provider Unresolved Events admin tab. Adjacent to
// the J15 user mapping tab. Reads via J3 providers / unmatched
// events / mapping context RPCs and writes via J7 reprocess RPC;
// never touches provider tables directly.
import { ProviderUnresolvedEventsAdminPanel } from '@/components/admin/ProviderUnresolvedEventsAdminPanel';
// Batch 18-J23 — Provider Audit History admin tab. Adjacent to the
// J18 unresolved events tab. Reads only via the J3 provider RPC +
// the J21 safe audit-history RPC; read-only — never mutates.
import { ProviderAuditHistoryAdminPanel } from '@/components/admin/ProviderAuditHistoryAdminPanel';
// Phase Q3 — Quiz Review & Approval admin panel (the training-quiz go-live gate).
import { QuizReviewAdminPanel } from '@/components/admin/QuizReviewAdminPanel';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { ChevronsUpDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  GraduationCap,
  BookOpen,
  ClipboardCheck,
  Loader2,
  ExternalLink,
  Pencil,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Filter,
  Link2,
  Building2,
  Rss,
  Inbox,
  Brain,
  Calendar,
  BarChart3,
  Trash2,
  Wrench,
  X,
  Sparkles,
  Bot,
  Zap,
  Upload,
  FileText,
  Wand2,
  Globe
} from 'lucide-react';
import { useRecommendedTraining, useArtifactJobMappings, useJobsCatalog, useArtifactJobMappingCounts, useArtifactFitmentCounts, useArtifactFitment, useArtifactTranscriptCounts } from '@/hooks/useLearningCenter';
import { useArtifactCapabilityMapCounts } from '@/hooks/useTrainingCapabilities';
import { useQueryClient } from '@tanstack/react-query';
import { useShopRequirements, useCreateRequirement, useUpdateRequirement } from '@/hooks/useTrainingRequirements';
import { useTrainingProviders } from '@/hooks/useTrainingProviders';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
// FF-C: training-catalog admin authority. Mirrors the canonical
// pattern used in DiagnosticFlowAdmin: feature flag exposes the
// admin UI; `useIsInternalAdmin()` is the actual authority. A
// CATALYST-tier manager who is NOT an internal admin can no longer
// reach the admin workflow even if the feature flag is enabled
// (e.g., via sessionStorage override).
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { BookChapterManagement } from '@/components/admin/BookChapterManagement';
import { useAuth } from '@/contexts/AuthContext';
import { 
  ARTIFACT_TYPE_LABELS, 
  ARTIFACT_TYPE_ICONS,
  ACCESS_TYPE_LABELS,
  SKILL_DEPTH_LABELS,
  CONFIDENCE_LEVEL_LABELS,
  type TrainingArtifact,
  type ArtifactType,
  type AccessType,
  type SkillDepth,
  type ConfidenceLevel,
  type DeliveryFormat,
  type TrainingRequirement,
} from '@/types/training';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Slider } from '@/components/ui/slider';
import { RequireFeature } from '@/components/access/RequireFeature';
import { ShowFeature } from '@/components/access/ShowFeature';
import { ProviderManagementTab } from '@/components/admin/ProviderManagementTab';
import { DiscoverySourcesTab } from '@/components/admin/DiscoverySourcesTab';
import { DiscoveryReviewTab } from '@/components/admin/DiscoveryReviewTab';
import { OfferingsManagementTab } from '@/components/admin/OfferingsManagementTab';
import { TrainingEffectivenessAdmin } from '@/components/admin/TrainingEffectivenessAdmin';
import { LearnedMappingsTab } from '@/components/admin/LearnedMappingsTab';
import { TrainingCatalogCsvImportDialog } from '@/components/admin/TrainingCatalogCsvImportDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EcosystemDirectory } from '@/components/admin/EcosystemDirectory';


// Helper component for Filters status column
function ConfigStatusFilters({ artifact }: { artifact: TrainingArtifact }) {
  const hasFuelTypes = artifact.vehicle_fuel_types && artifact.vehicle_fuel_types.length > 0;
  const hasKeywords = artifact.keywords && artifact.keywords.length > 0;
  const hasDtcCodes = artifact.dtc_codes && artifact.dtc_codes.length > 0;
  const filterCount = [hasFuelTypes, hasKeywords, hasDtcCodes].filter(Boolean).length;

  const tooltipParts: string[] = [];
  if (hasFuelTypes) tooltipParts.push(`Fuel: ${artifact.vehicle_fuel_types!.join(', ')}`);
  if (hasKeywords) tooltipParts.push(`Keywords: ${artifact.keywords!.length}`);
  if (hasDtcCodes) tooltipParts.push(`DTCs: ${artifact.dtc_codes!.length}`);

  if (filterCount === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <XCircle className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">No filters configured</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const isPartial = filterCount < 3;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {isPartial ? (
          <AlertCircle className="h-4 w-4 text-amber-500 cursor-help" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-green-500 cursor-help" />
        )}
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs space-y-0.5">
          {tooltipParts.map((part, i) => (
            <p key={i}>{part}</p>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component for Jobs status column
function ConfigStatusJobs({ artifactId, jobMappingCounts }: { 
  artifactId: string; 
  jobMappingCounts: Map<string, number> | undefined;
}) {
  const count = jobMappingCounts?.get(artifactId) || 0;

  if (count === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-help">
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Not mapped to any jobs</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-help">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-xs text-muted-foreground">{count}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">Mapped to {count} job{count !== 1 ? 's' : ''}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component for Vehicle Fitment status column
function ConfigStatusVehicle({ artifactId, fitmentCounts }: { 
  artifactId: string; 
  fitmentCounts: Map<string, number> | undefined;
}) {
  const count = fitmentCounts?.get(artifactId) || 0;

  if (count === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-help">
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">No vehicle fitment configured</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-help">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-xs text-muted-foreground">{count}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{count} vehicle fitment{count !== 1 ? 's' : ''} configured</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component for Sidebar Eligibility status column
// Uses the training_artifact_sidebar_eligibility view
function ConfigStatusSidebar({ 
  artifactId, 
  jobMappingCounts, 
  fitmentCounts 
}: { 
  artifactId: string; 
  jobMappingCounts: Map<string, number> | undefined;
  fitmentCounts: Map<string, number> | undefined;
}) {
  // Sidebar eligibility requires BOTH job mappings AND vehicle fitment with engine
  const hasJobMappings = (jobMappingCounts?.get(artifactId) || 0) > 0;
  const hasFitment = (fitmentCounts?.get(artifactId) || 0) > 0;
  
  // Note: This is a simplified check - the DB view checks for engine specifically
  // For full accuracy, we'd need to query the eligibility view
  const isEligible = hasJobMappings && hasFitment;

  if (!isEligible) {
    const missingParts: string[] = [];
    if (!hasJobMappings) missingParts.push('job mappings');
    if (!hasFitment) missingParts.push('vehicle fitment with engine');
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-help">
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs space-y-1">
            <p className="font-medium">Not eligible for in-bay sidebar</p>
            <p>Missing: {missingParts.join(', ')}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-help">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">✓ Eligible for in-bay "Help with this Job" sidebar</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component for Transcript status column
function ConfigStatusTranscript({ 
  artifactId, 
  transcriptCounts 
}: { 
  artifactId: string; 
  transcriptCounts: Map<string, boolean> | undefined;
}) {
  const hasTranscript = transcriptCounts?.get(artifactId) || false;

  if (!hasTranscript) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <XCircle className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">No transcript saved</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CheckCircle2 className="h-4 w-4 text-green-500 cursor-help" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">Transcript available</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component for Capabilities count column
function ConfigStatusCaps({ artifactId, capabilityCounts }: { 
  artifactId: string; 
  capabilityCounts: Map<string, number> | undefined;
}) {
  const count = capabilityCounts?.get(artifactId) || 0;

  if (count === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <XCircle className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">No capabilities mapped</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-help">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-xs text-muted-foreground">{count}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{count} capabilit{count !== 1 ? 'ies' : 'y'} mapped</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * FF-C — Training Catalog Admin (entry-point wrapper).
 *
 * Mirrors `DiagnosticFlowAdmin`'s authority pattern:
 *   1. Feature flag (`learningCenter_catalogAdmin`) gates UI exposure.
 *   2. `useIsInternalAdmin()` is the actual authority.
 *
 * Both must be true to render the admin workflow content. The route
 * itself is also gated by `<ProtectedRoute requiredRoles={['MANAGER',
 * 'OWNER']}>` in `App.tsx`, but that role gate alone is insufficient
 * for global training-catalog administration; non-admin shop managers
 * must NOT reach this workflow even when the feature flag is on.
 */
export default function TrainingCatalogAdmin() {
  const isEnabled = useFeatureFlag('learningCenter_catalogAdmin');
  const { data: isInternalAdmin, isLoading: checkingAdmin } = useIsInternalAdmin();

  if (!isEnabled) {
    return (
      <div className="container max-w-6xl py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Training Catalog Admin</h2>
            <p className="text-muted-foreground">
              Training Catalog administration is not enabled for this shop.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingAdmin) {
    return (
      <div className="container max-w-6xl py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isInternalAdmin) {
    return (
      <div className="container max-w-6xl py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Not Authorized</h2>
            <p className="text-muted-foreground">
              Training catalog administration is restricted to internal administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <TrainingCatalogAdminContent />;
}

function TrainingCatalogAdminContent() {
  const navigate = useNavigate();
  const { activeShop: currentShop, user: authUser } = useAuth();
  const canManageRequirements = useFeatureFlag('learningCenter_requirements');
  const canManageBooks = useFeatureFlag('trainingBookChapters');
  
  // Persist tab state in sessionStorage
  const [activeTab, setActiveTab] = useState(() => 
    sessionStorage.getItem('training-catalog-tab') || 'catalog'
  );
  const [searchQuery, setSearchQuery] = useState(() => 
    sessionStorage.getItem('training-catalog-search') || ''
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [requirementDialogOpen, setRequirementDialogOpen] = useState(false);
  // Batch 18-F48 — edit-existing-requirement flow.
  const [editRequirementOpen, setEditRequirementOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] =
    useState<TrainingRequirement | null>(null);
  const [isGeneratingSummaries, setIsGeneratingSummaries] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState<{ current: number; total: number } | null>(null);
  const [isAutoMapping, setIsAutoMapping] = useState(false);
  
  // Save tab state when it changes
  useEffect(() => {
    sessionStorage.setItem('training-catalog-tab', activeTab);
  }, [activeTab]);

  // Save search query when it changes
  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem('training-catalog-search', searchQuery);
    } else {
      sessionStorage.removeItem('training-catalog-search');
    }
  }, [searchQuery]);
  
  const queryClient = useQueryClient();
  const { data: artifactsResult, isLoading, refetch } = useRecommendedTraining({ pageSize: 500 });
  const artifacts = artifactsResult?.data;
  const { data: jobMappingCounts } = useArtifactJobMappingCounts();
  const { data: fitmentCounts } = useArtifactFitmentCounts();
  const { data: transcriptCounts } = useArtifactTranscriptCounts();
  const { data: capabilityCounts } = useArtifactCapabilityMapCounts();
  const { data: requirements } = useShopRequirements(currentShop?.id);
  const createRequirement = useCreateRequirement();

  // Bulk AI analyze - processes all artifacts with transcripts
  const handleBulkAnalyze = async () => {
    // Count artifacts that have transcripts
    const artifactsWithTranscripts = artifacts?.filter(a => transcriptCounts?.get(a.id)) || [];
    
    if (artifactsWithTranscripts.length === 0) {
      toast.info('No artifacts with transcripts to analyze');
      return;
    }

    setIsAnalyzing(true);
    setAnalyzeProgress({ current: 0, total: artifactsWithTranscripts.length });
    
    try {
      const { data, error } = await supabase.functions.invoke('bulk-analyze-training', {
        body: { limit: 100, force_reanalyze: false },
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      const { processed, succeeded, failed } = data;
      if (processed === 0) {
        toast.info('All artifacts with transcripts have been analyzed');
      } else {
        toast.success(
          `AI Analysis complete: ${succeeded} artifacts processed${failed > 0 ? `, ${failed} failed` : ''}`,
          { duration: 5000 }
        );
        refetch();
      }
    } catch (error) {
      console.error('Failed to bulk analyze:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to analyze';
      if (errorMsg.includes('429') || errorMsg.includes('rate')) {
        toast.error('Rate limit reached. Please try again in a few minutes.');
      } else if (errorMsg.includes('402')) {
        toast.error('AI credits exhausted. Please add credits to continue.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsAnalyzing(false);
      setAnalyzeProgress(null);
    }
  };

  // Auto-map capabilities for all unmapped artifacts
  const handleAutoMapCapabilities = async () => {
    setIsAutoMapping(true);
    try {
      const { data, error } = await supabase.functions.invoke('bulk-map-capabilities', {
        body: { limit: 300 },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const { processed, mapped_by_rules, mapped_by_ai, zero_results } = data;
      if (processed === 0) {
        toast.success('All artifacts already have capabilities mapped!');
      } else {
        toast.success(
          `Mapped capabilities for ${mapped_by_rules + mapped_by_ai} of ${processed} artifacts` +
          ` (${mapped_by_rules} by rules, ${mapped_by_ai} by AI` +
          (zero_results > 0 ? `, ${zero_results} still unresolved` : '') + ')',
          { duration: 8000 }
        );
      }

      // Refresh capability counts
      queryClient.invalidateQueries({ queryKey: ['artifact-capability-map-counts'] });
    } catch (err) {
      console.error('Auto-map capabilities failed:', err);
      const msg = err instanceof Error ? err.message : 'Failed to auto-map capabilities';
      if (msg.includes('429') || msg.includes('rate')) {
        toast.error('Rate limit reached. Please try again in a few minutes.');
      } else if (msg.includes('402')) {
        toast.error('AI credits exhausted. Please add credits to continue.');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsAutoMapping(false);
    }
  };

  // Filter artifacts by search (and exclude chapters — managed in Books tab)
  const filteredArtifacts = artifacts?.filter((artifact) => {
    // Chapters are managed in the Books tab, not in the main catalog
    if ((artifact as any).artifact_type === 'chapter') return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      artifact.title.toLowerCase().includes(query) ||
      artifact.provider_name?.toLowerCase().includes(query) ||
      artifact.instructor_name?.toLowerCase().includes(query)
    );
  }) || [];

  // Sort artifacts: most checkmarks (filters, jobs, vehicle, active) at top
  const sortedArtifacts = [...filteredArtifacts].sort((a, b) => {
    // Calculate score for each artifact (higher = more complete)
    const getScore = (artifact: TrainingArtifact) => {
      let score = 0;
      // Active status (+1)
      if (artifact.is_active) score += 1;
      // Has filters: fuel types, keywords, or DTCs (+1)
      const hasFilters = 
        (artifact.vehicle_fuel_types && artifact.vehicle_fuel_types.length > 0) ||
        (artifact.keywords && artifact.keywords.length > 0) ||
        (artifact.dtc_codes && artifact.dtc_codes.length > 0);
      if (hasFilters) score += 1;
      // Has job mappings (+1)
      const jobCount = jobMappingCounts?.get(artifact.id) || 0;
      if (jobCount > 0) score += 1;
      // Has vehicle fitment (+1)
      const fitmentCount = fitmentCounts?.get(artifact.id) || 0;
      if (fitmentCount > 0) score += 1;
      return score;
    };
    
    const scoreA = getScore(a);
    const scoreB = getScore(b);
    
    // Sort descending (higher score first)
    if (scoreB !== scoreA) return scoreB - scoreA;
    
    // Secondary sort by title for stability
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 container mx-auto p-4 md:p-6 max-w-full pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-primary" />
              Training Catalog
            </h1>
            <p className="text-muted-foreground text-sm">
              Manage training resources and requirements
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/shop-profile?tab=admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <CreateArtifactDialog 
              open={createDialogOpen} 
              onOpenChange={setCreateDialogOpen}
              onCreated={() => refetch()}
            />
            <TrainingCatalogCsvImportDialog
              open={csvImportOpen}
              onOpenChange={setCsvImportOpen}
              onImported={() => refetch()}
            />
          </div>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="catalog" className="gap-2">
            <BookOpen className="h-4 w-4" />
            Catalog
          </TabsTrigger>
          {canManageBooks && (
            <TabsTrigger value="books" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Books
            </TabsTrigger>
          )}
          <ShowFeature feature="ui_trainingProviderAdmin">
            <TabsTrigger value="providers" className="gap-2">
              <Building2 className="h-4 w-4" />
              Providers
            </TabsTrigger>
          </ShowFeature>
          {/* Batch 18-J9 — Training Providers registry tab. Distinct
              from the existing "Providers" tab above (which is for
              the artifact-provenance catalog). The new tab surfaces
              the I3/J-lane provider evidence callback registry. */}
          <TabsTrigger value="training-providers" className="gap-2" data-testid="training-providers-admin-tab">
            <Building2 className="h-4 w-4" />
            Training Providers
          </TabsTrigger>
          {/* Batch 18-J12 — Provider Artifact Mapping admin tab. */}
          <TabsTrigger
            value="provider-artifact-mapping"
            className="gap-2"
            data-testid="provider-artifact-mapping-admin-tab"
          >
            <Link2 className="h-4 w-4" />
            Provider Artifact Mapping
          </TabsTrigger>
          {/* Batch 18-J15 — Provider User Mapping admin tab. */}
          <TabsTrigger
            value="provider-user-mapping"
            className="gap-2"
            data-testid="provider-user-mapping-admin-tab"
          >
            <Link2 className="h-4 w-4" />
            Provider User Mapping
          </TabsTrigger>
          {/* Batch 18-J18 — Provider Unresolved Events admin tab. */}
          <TabsTrigger
            value="provider-unresolved-events"
            className="gap-2"
            data-testid="provider-unresolved-events-admin-tab"
          >
            <Inbox className="h-4 w-4" />
            Provider Unresolved Events
          </TabsTrigger>
          {/* Batch 18-J23 — Provider Audit History admin tab. */}
          <TabsTrigger
            value="provider-audit-history"
            className="gap-2"
            data-testid="provider-audit-history-admin-tab"
          >
            <ClipboardCheck className="h-4 w-4" />
            Provider Audit History
          </TabsTrigger>
          <ShowFeature feature="ui_trainingDiscoveryAdmin">
            <TabsTrigger value="sources" className="gap-2">
              <Rss className="h-4 w-4" />
              Sources
            </TabsTrigger>
            <TabsTrigger value="discovery" className="gap-2">
              <Inbox className="h-4 w-4" />
              Discovery
            </TabsTrigger>
          </ShowFeature>
          <ShowFeature feature="ui_trainingCatalogAdmin">
            <TabsTrigger value="offerings" className="gap-2">
              <Calendar className="h-4 w-4" />
              Offerings
            </TabsTrigger>
            <TabsTrigger value="signals" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Signals
            </TabsTrigger>
          </ShowFeature>
          <ShowFeature feature="ui_trainingLearnedMappings">
            <TabsTrigger value="learned" className="gap-2">
              <Zap className="h-4 w-4" />
              Learned
            </TabsTrigger>
          </ShowFeature>
          <ShowFeature feature="ui_ecosystemAdmin">
            <TabsTrigger value="ecosystem" className="gap-2">
              <Globe className="h-4 w-4" />
              Ecosystem
            </TabsTrigger>
          </ShowFeature>
          {canManageRequirements && (
            <TabsTrigger value="requirements" className="gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Requirements
            </TabsTrigger>
          )}
          {/* Phase Q3 — Quiz Review & Approval. Not gated on trainingQuizMvp
              (that flag only gates technician-facing quiz UI); this admin
              review surface inherits the page's InternalAdmin gates. */}
          <TabsTrigger value="quiz-review" className="gap-2" data-testid="quiz-review-admin-tab">
            <GraduationCap className="h-4 w-4" />
            Quiz Review
          </TabsTrigger>
        </TabsList>

        {/* Catalog Tab */}
        <TabsContent value="catalog">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Training Artifacts</CardTitle>
                  <CardDescription>
                    {sortedArtifacts.length} training resources
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-[250px]"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={handleBulkAnalyze}
                    disabled={isAnalyzing}
                    title="Run AI analysis on all artifacts with transcripts to extract summaries, keywords, job mappings, and vehicle fitment"
                  >
                    {isAnalyzing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Brain className="h-4 w-4 mr-2" />
                    )}
                    {isAnalyzing 
                      ? analyzeProgress 
                        ? `Analyzing ${analyzeProgress.current}/${analyzeProgress.total}...` 
                        : 'Analyzing...' 
                      : 'AI Analyze'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAutoMapCapabilities}
                    disabled={isAutoMapping}
                    title="Auto-map capabilities for all unmapped artifacts using rule engine + AI fallback"
                  >
                    {isAutoMapping ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {isAutoMapping ? 'Mapping...' : 'Auto-Map Caps'}
                  </Button>
                  <Button variant="outline" onClick={() => setCsvImportOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </Button>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Artifact
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : sortedArtifacts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No training artifacts found</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Artifact
                  </Button>
                </div>
              ) : (
                <div 
                  className="max-h-[500px] overflow-auto cursor-grab active:cursor-grabbing select-none"
                  onMouseDown={(e) => {
                    const el = e.currentTarget;
                    el.dataset.isDragging = 'true';
                    el.dataset.startX = String(e.pageX - el.offsetLeft);
                    el.dataset.startY = String(e.pageY - el.offsetTop);
                    el.dataset.scrollLeft = String(el.scrollLeft);
                    el.dataset.scrollTop = String(el.scrollTop);
                  }}
                  onMouseMove={(e) => {
                    const el = e.currentTarget;
                    if (el.dataset.isDragging !== 'true') return;
                    e.preventDefault();
                    const x = e.pageX - el.offsetLeft;
                    const y = e.pageY - el.offsetTop;
                    const walkX = x - Number(el.dataset.startX);
                    const walkY = y - Number(el.dataset.startY);
                    el.scrollLeft = Number(el.dataset.scrollLeft) - walkX;
                    el.scrollTop = Number(el.dataset.scrollTop) - walkY;
                  }}
                  onMouseUp={(e) => { e.currentTarget.dataset.isDragging = 'false'; }}
                  onMouseLeave={(e) => { e.currentTarget.dataset.isDragging = 'false'; }}
                >
                  <div className="min-w-max">
                    <table className="w-full caption-bottom text-sm">
                    <thead className="[&_tr]:border-b">
                      <tr className="border-b transition-colors hover:bg-muted/50">
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[300px] sticky left-0 bg-background z-10">Title</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[120px]">Provider</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[120px]">Instructor</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[80px]">Type</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[70px]">Access</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[90px]">Confidence</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Filters</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Jobs</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Vehicle</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Sidebar</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[70px]">Transcript</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Caps</th>
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground min-w-[60px]">Active</th>
                        <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground min-w-[100px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {sortedArtifacts.map((artifact) => (
                        <tr key={artifact.id} className="border-b transition-colors hover:bg-muted/50">
                          <td className="p-4 align-middle sticky left-0 bg-background">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {ARTIFACT_TYPE_ICONS[artifact.artifact_type]}
                              </span>
                              <span className="font-medium truncate max-w-[350px]">
                                {artifact.title}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 align-middle text-muted-foreground">
                            {artifact.provider_name || '—'}
                          </td>
                          <td className="p-4 align-middle text-muted-foreground">
                            {artifact.instructor_name || '—'}
                          </td>
                          <td className="p-4 align-middle">
                            <Badge variant="outline" className="text-xs">
                              {ARTIFACT_TYPE_LABELS[artifact.artifact_type]}
                            </Badge>
                          </td>
                          <td className="p-4 align-middle">
                            <Badge 
                              variant={artifact.access_type === 'free' ? 'default' : 'secondary'}
                              className={cn("text-xs", artifact.access_type === 'free' && "bg-green-600")}
                            >
                              {ACCESS_TYPE_LABELS[artifact.access_type]}
                            </Badge>
                          </td>
                          <td className="p-4 align-middle">
                            <Badge 
                              variant={artifact.confidence_level === 'optibay_verified' ? 'default' : 'outline'}
                              className="text-xs"
                            >
                              {CONFIDENCE_LEVEL_LABELS[artifact.confidence_level]}
                            </Badge>
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusFilters artifact={artifact} />
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusJobs artifactId={artifact.id} jobMappingCounts={jobMappingCounts} />
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusVehicle artifactId={artifact.id} fitmentCounts={fitmentCounts} />
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusSidebar 
                              artifactId={artifact.id} 
                              jobMappingCounts={jobMappingCounts}
                              fitmentCounts={fitmentCounts}
                            />
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusTranscript 
                              artifactId={artifact.id} 
                              transcriptCounts={transcriptCounts}
                            />
                          </td>
                          <td className="p-4 align-middle">
                            <ConfigStatusCaps artifactId={artifact.id} capabilityCounts={capabilityCounts} />
                          </td>
                          <td className="p-4 align-middle">
                            {artifact.is_active ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-muted-foreground" />
                            )}
                          </td>
                          <td className="p-4 align-middle text-right">
                            <div className="flex items-center justify-end gap-2">
                              {((artifact.artifact_type === 'book' || artifact.artifact_type === 'ebook') ? artifact.purchase_url : artifact.access_url) ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  asChild
                                >
                                  <a 
                                    href={(artifact.artifact_type === 'book' || artifact.artifact_type === 'ebook') ? artifact.purchase_url! : artifact.access_url!} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    title={(artifact.artifact_type === 'book' || artifact.artifact_type === 'ebook') ? `Buy from ${artifact.purchase_provider_name || 'store'}` : 'Open resource'}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  disabled
                                  title="No URL available"
                                >
                                  <ExternalLink className="h-4 w-4 opacity-30" />
                                </Button>
                              )}
                              <EditArtifactDialog artifact={artifact} onUpdated={() => refetch()} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requirements Tab (Catalyst only) */}
        {canManageRequirements && (
          <TabsContent value="requirements">
            <RequireFeature feature="learningCenter_requirements">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Training Requirements</CardTitle>
                      <CardDescription>
                        Assign required training to roles and teams
                      </CardDescription>
                    </div>
                    <CreateRequirementDialog
                      open={requirementDialogOpen}
                      onOpenChange={setRequirementDialogOpen}
                      shopId={currentShop?.id || ''}
                      artifacts={artifacts || []}
                    />
                    {/* Batch 18-F48 — edit-existing-requirement dialog */}
                    <EditRequirementDialog
                      open={editRequirementOpen}
                      onOpenChange={(next) => {
                        setEditRequirementOpen(next);
                        if (!next) setEditingRequirement(null);
                      }}
                      requirement={editingRequirement}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {!requirements || requirements.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No training requirements configured</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setRequirementDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Requirement
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Training</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Domain</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {requirements.map((req) => (
                          <TableRow key={req.id}>
                            <TableCell>
                              {(req as any).artifact?.title || 'Any matching training'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {req.role_key || 'All roles'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {req.domain_key?.replace(/_/g, ' ') || '-'}
                            </TableCell>
                            <TableCell>
                              {req.required_by_date || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1.5">
                                {req.is_active ? (
                                  <Badge variant="default">Active</Badge>
                                ) : (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                                {/* Batch 18-F46 — manager-verification gate badge */}
                                {req.requires_manager_verification === true && (
                                  <Badge
                                    variant="outline"
                                    data-testid={`training-requirement-manager-verification-badge-${req.id}`}
                                  >
                                    Manager verification required
                                  </Badge>
                                )}
                                {/* Batch 18-F48 — row-level Edit trigger */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  aria-label={`Edit requirement${
                                    (req as TrainingRequirement).artifact?.title
                                      ? ` for ${(req as TrainingRequirement).artifact?.title}`
                                      : ''
                                  }`}
                                  data-testid={`training-requirement-edit-trigger-${req.id}`}
                                  onClick={() => {
                                    setEditingRequirement(req as TrainingRequirement);
                                    setEditRequirementOpen(true);
                                  }}
                                >
                                  Edit
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </RequireFeature>
          </TabsContent>
        )}

        {/* Providers Tab - Phase 4 */}
        <ShowFeature feature="ui_trainingProviderAdmin">
          <TabsContent value="providers">
            <ProviderManagementTab />
          </TabsContent>
        </ShowFeature>

        {/* Sources Tab - Phase 1 */}
        <ShowFeature feature="ui_trainingDiscoveryAdmin">
          <TabsContent value="sources">
            <DiscoverySourcesTab />
          </TabsContent>
        </ShowFeature>

        {/* Discovery Review Tab - Phase 1 */}
        <ShowFeature feature="ui_trainingDiscoveryAdmin">
          <TabsContent value="discovery">
            <DiscoveryReviewTab />
          </TabsContent>
        </ShowFeature>

        {/* Offerings Tab - Phase 2 */}
        <ShowFeature feature="ui_trainingCatalogAdmin">
          <TabsContent value="offerings">
            <OfferingsManagementTab />
          </TabsContent>
        </ShowFeature>

        {/* Engagement Signals Tab - Phase 3 */}
        <ShowFeature feature="ui_trainingCatalogAdmin">
          <TabsContent value="signals">
            <TrainingEffectivenessAdmin shopId={currentShop?.id} />
          </TabsContent>
        </ShowFeature>

        {/* Learned Mappings Tab - OptiBay Phase 3 */}
        <ShowFeature feature="ui_trainingLearnedMappings">
          <TabsContent value="learned">
            <LearnedMappingsTab />
          </TabsContent>
        </ShowFeature>

        {/* Ecosystem Tab */}
        <ShowFeature feature="ui_ecosystemAdmin">
          <TabsContent value="ecosystem">
            <EcosystemDirectory />
          </TabsContent>
        </ShowFeature>

        {/* Books Tab - MVP 1 Book/Chapter Hierarchy */}
        {canManageBooks && (
          <TabsContent value="books">
            <BookChapterManagement />
          </TabsContent>
        )}

        {/* Batch 18-J9 — Training Providers registry panel. */}
        <TabsContent value="training-providers">
          <TrainingProvidersAdminPanel
            orgId={currentShop?.organization_id ?? null}
            shopId={currentShop?.id ?? null}
            currentUserId={authUser?.id ?? null}
          />
        </TabsContent>

        {/* Batch 18-J12 — Provider Artifact Mapping admin panel.
            Mount point test-id is set on the panel component
            itself: `provider-artifact-mapping-admin-panel`. */}
        <TabsContent value="provider-artifact-mapping">
          <ProviderArtifactMappingAdminPanel
            orgId={currentShop?.organization_id ?? null}
            shopId={currentShop?.id ?? null}
            currentUserId={authUser?.id ?? null}
          />
        </TabsContent>

        {/* Batch 18-J15 — Provider User Mapping admin panel.
            Mount point test-id is set on the panel component
            itself: `provider-user-mapping-admin-panel`. */}
        <TabsContent value="provider-user-mapping">
          <ProviderUserMappingAdminPanel
            orgId={currentShop?.organization_id ?? null}
            shopId={currentShop?.id ?? null}
            currentUserId={authUser?.id ?? null}
          />
        </TabsContent>

        {/* Batch 18-J18 — Provider Unresolved Events admin panel.
            Mount point test-id is set on the panel component
            itself: `provider-unresolved-events-admin-panel`. */}
        <TabsContent value="provider-unresolved-events">
          <ProviderUnresolvedEventsAdminPanel
            orgId={currentShop?.organization_id ?? null}
            shopId={currentShop?.id ?? null}
            currentUserId={authUser?.id ?? null}
          />
        </TabsContent>

        {/* Batch 18-J23 — Provider Audit History admin panel.
            Read-only — mount point test-id is set on the panel
            component itself: `provider-audit-history-admin-panel`. */}
        <TabsContent value="provider-audit-history">
          <ProviderAuditHistoryAdminPanel
            orgId={currentShop?.organization_id ?? null}
            shopId={currentShop?.id ?? null}
            currentUserId={authUser?.id ?? null}
          />
        </TabsContent>

        {/* Phase Q3 — Quiz Review & Approval panel. Manager-only draft review +
            approval; never enables the technician quiz flag. */}
        <TabsContent value="quiz-review">
          <QuizReviewAdminPanel />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}

// Create Artifact Dialog
function CreateArtifactDialog({ 
  open, 
  onOpenChange,
  onCreated 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: providers } = useTrainingProviders();
  const [formData, setFormData] = useState({
    title: '',
    provider_id: '' as string,
    provider_name: '',
    instructor_name: '',
    artifact_type: 'short_video' as ArtifactType,
    access_url: '',
    access_type: 'free' as AccessType,
    estimated_time_minutes: '',
    skill_depth: 'foundation' as SkillDepth,
    confidence_level: 'provisional' as ConfidenceLevel,
    delivery_format: 'on_demand' as DeliveryFormat,
    travel_required: false,
    original_summary_short: '',
  });

  const handleSubmit = async () => {
    if (!formData.title || !formData.access_url) {
      toast.error('Title and URL are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('training_artifacts')
        .insert({
          title: formData.title,
          provider_id: formData.provider_id || null,
          provider_name: formData.provider_name || null,
          instructor_name: formData.instructor_name || null,
          artifact_type: formData.artifact_type,
          access_url: formData.access_url,
          access_type: formData.access_type,
          estimated_time_minutes: formData.estimated_time_minutes 
            ? parseInt(formData.estimated_time_minutes) 
            : null,
          skill_depth: formData.skill_depth,
          confidence_level: formData.confidence_level,
          delivery_format: formData.delivery_format,
          travel_required: formData.travel_required,
          original_summary_short: formData.original_summary_short || null,
          is_active: true,
        });

      if (error) throw error;

      toast.success('Training artifact created');
      onOpenChange(false);
      onCreated();
      
      // Reset form
      setFormData({
        title: '',
        provider_id: '',
        provider_name: '',
        instructor_name: '',
        artifact_type: 'short_video',
        access_url: '',
        access_type: 'free',
        estimated_time_minutes: '',
        skill_depth: 'foundation',
        confidence_level: 'provisional',
        delivery_format: 'on_demand',
        travel_required: false,
        original_summary_short: '',
      });
    } catch (error) {
      toast.error('Failed to create artifact');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Training Artifact</DialogTitle>
          <DialogDescription>
            Add a new training resource to the catalog (metadata only, no hosting).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Brake System Fundamentals"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select 
                value={formData.provider_id} 
                onValueChange={(v) => {
                  const selectedProvider = providers?.find(p => p.id === v);
                  setFormData(prev => ({ 
                    ...prev, 
                    provider_id: v,
                    provider_name: selectedProvider?.display_name || prev.provider_name
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers?.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.display_name}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other (manual entry)</SelectItem>
                </SelectContent>
              </Select>
              {formData.provider_id === 'other' && (
                <Input
                  className="mt-2"
                  value={formData.provider_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider_name: e.target.value }))}
                  placeholder="Enter provider name..."
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="instructor">Instructor</Label>
              <Input
                id="instructor"
                value={formData.instructor_name}
                onChange={(e) => setFormData(prev => ({ ...prev, instructor_name: e.target.value }))}
                placeholder="e.g., Paul Danner"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">Access URL *</Label>
            <Input
              id="url"
              value={formData.access_url}
              onChange={(e) => setFormData(prev => ({ ...prev, access_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={formData.artifact_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, artifact_type: v as ArtifactType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ARTIFACT_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Access</Label>
              <Select 
                value={formData.access_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, access_type: v as AccessType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCESS_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Skill Depth</Label>
              <Select 
                value={formData.skill_depth} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, skill_depth: v as SkillDepth }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SKILL_DEPTH_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Confidence</Label>
              <Select 
                value={formData.confidence_level} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, confidence_level: v as ConfidenceLevel }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONFIDENCE_LEVEL_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="time">Estimated Time (minutes)</Label>
            <Input
              id="time"
              type="number"
              value={formData.estimated_time_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, estimated_time_minutes: e.target.value }))}
              placeholder="e.g., 45"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">
              OptiBay Summary 
              <span className="text-muted-foreground ml-1">
                ({formData.original_summary_short.length}/200)
              </span>
            </Label>
            <Textarea
              id="summary"
              value={formData.original_summary_short}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                original_summary_short: e.target.value.slice(0, 200) 
              }))}
              placeholder="Brief OptiBay-authored summary for recommendations..."
              maxLength={200}
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="travel"
              checked={formData.travel_required}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, travel_required: !!checked }))}
            />
            <Label htmlFor="travel" className="text-sm">Travel required</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Artifact'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Create Requirement Dialog
function CreateRequirementDialog({ 
  open, 
  onOpenChange,
  shopId,
  artifacts 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  shopId: string;
  artifacts: TrainingArtifact[];
}) {
  const createRequirement = useCreateRequirement();
  const [formData, setFormData] = useState({
    artifactId: '',
    roleKey: '',
    domainKey: '',
    requiredByDate: '',
  });
  // Batch 18-F46 — admin-configurable manager-verification gate.
  // Default unchecked; resets after successful create.
  const [requiresManagerVerification, setRequiresManagerVerification] =
    useState(false);

  const handleSubmit = async () => {
    try {
      await createRequirement.mutateAsync({
        orgId: shopId,
        artifactId: formData.artifactId || undefined,
        roleKey: formData.roleKey || undefined,
        domainKey: formData.domainKey || undefined,
        requiredByDate: formData.requiredByDate || undefined,
        requiresManagerVerification,
      });
      toast.success('Requirement created');
      onOpenChange(false);
      setFormData({ artifactId: '', roleKey: '', domainKey: '', requiredByDate: '' });
      setRequiresManagerVerification(false);
    } catch (error) {
      toast.error('Failed to create requirement');
    }
  };

  return (
    <>
      <Button onClick={() => onOpenChange(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Add Requirement
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Training Requirement</DialogTitle>
            <DialogDescription>
              Assign required training for roles or teams.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Training Artifact</Label>
              <Select 
                value={formData.artifactId} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, artifactId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select training..." />
                </SelectTrigger>
                <SelectContent>
                  {artifacts.map((artifact) => (
                    <SelectItem key={artifact.id} value={artifact.id}>
                      {artifact.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Role (optional)</Label>
              <Select 
                value={formData.roleKey} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, roleKey: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technician">Technician</SelectItem>
                  <SelectItem value="advisor">Service Advisor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date (optional)</Label>
              <Input
                type="date"
                value={formData.requiredByDate}
                onChange={(e) => setFormData(prev => ({ ...prev, requiredByDate: e.target.value }))}
              />
            </div>

            {/* Batch 18-F46 — manager-verification gate */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="training-requirement-requires-manager-verification"
                data-testid="training-requirement-requires-manager-verification-checkbox"
                checked={requiresManagerVerification}
                onCheckedChange={(checked) =>
                  setRequiresManagerVerification(checked === true)
                }
              />
              <div className="space-y-1 leading-tight">
                <Label
                  htmlFor="training-requirement-requires-manager-verification"
                  className="cursor-pointer"
                >
                  Require manager verification for completion
                </Label>
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="training-requirement-requires-manager-verification-helper"
                >
                  When enabled, a technician's manual completion will show as pending until a manager verifies it.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createRequirement.isPending}>
              {createRequirement.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Batch 18-F48 — flag-only edit dialog for existing requirements.
// Mirrors the F46 CreateRequirementDialog copy (label + helper) and
// adds a retroactive-effect warning paragraph above the buttons.
// No editable inputs for artifact / role / domain / due date — those
// stay locked to their create-time values; F48 ships flag-only per
// F47 §5.
function EditRequirementDialog({
  open,
  onOpenChange,
  requirement,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirement: TrainingRequirement | null;
}) {
  const updateRequirement = useUpdateRequirement();
  const [requiresManagerVerification, setRequiresManagerVerification] =
    useState(false);

  // Seed local state when the dialog opens or the selected
  // requirement changes. The default reflects the existing row
  // value (`true` flips the checkbox on; everything else
  // — `false` or undefined — leaves it off).
  useEffect(() => {
    if (open && requirement) {
      setRequiresManagerVerification(
        requirement.requires_manager_verification === true,
      );
    }
  }, [open, requirement]);

  const handleSave = async () => {
    if (!requirement) return;
    try {
      await updateRequirement.mutateAsync({
        requirementId: requirement.id,
        updates: {
          requiresManagerVerification,
        },
      });
      toast.success('Requirement updated');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update requirement');
    }
  };

  const artifactTitle =
    (requirement as TrainingRequirement | null)?.artifact?.title ??
    'Any matching training';
  const role = requirement?.role_key ?? 'All roles';
  const domain = requirement?.domain_key?.replace(/_/g, ' ') ?? '-';
  const dueDate = requirement?.required_by_date ?? '-';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="training-requirement-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit requirement</DialogTitle>
          <DialogDescription>
            Update the manager-verification policy for this requirement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Artifact</span>
              <span className="font-medium text-right">{artifactTitle}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium text-right">{role}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Domain</span>
              <span className="font-medium text-right">{domain}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Due date</span>
              <span className="font-medium text-right">{dueDate}</span>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="training-requirement-edit-requires-manager-verification"
              data-testid="training-requirement-edit-checkbox"
              checked={requiresManagerVerification}
              onCheckedChange={(checked) =>
                setRequiresManagerVerification(checked === true)
              }
            />
            <div className="space-y-1 leading-tight">
              <Label
                htmlFor="training-requirement-edit-requires-manager-verification"
                className="cursor-pointer"
              >
                Require manager verification for completion
              </Label>
              <p
                className="text-xs text-muted-foreground"
                data-testid="training-requirement-edit-helper"
              >
                When enabled, a technician's manual completion will show as pending until a manager verifies it.
              </p>
              <p
                className="text-xs text-amber-600"
                data-testid="training-requirement-edit-warning"
              >
                Changing this may affect existing manual completions for this requirement.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="training-requirement-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateRequirement.isPending || !requirement}
            data-testid="training-requirement-edit-save"
          >
            {updateRequirement.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Save requirement'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export EditArtifactDialog from the shared component
import { EditTrainingArtifactDialog as EditArtifactDialog } from '@/components/training/EditTrainingArtifactDialog';

export { EditArtifactDialog };
